import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { ExecutableVersionPort } from '../../../app/ports/organization/executable-version.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastV2Service, CoreUIToastDirection } from '../../services/toast.service';
import { OrgSdk } from 'lib-klab-sdks';
import { ExecutableVersion } from '../../../domain/models/organization/executable-version.model';
import { mapSDKExecutableVersionToDomain } from '../../mappers/organization/executable-version.mapper';
import { mapOrganizationErrorToMessage } from '../../utils/organization-error.mapper';
import { EnvMicroOrganization } from '../../config/env.micro.organization';
import { ORG_ENV } from '../../config/micros-env.tokens';
import { executableVersionSdkMock } from '../../mocks/organization/executable-version.mock';

@Injectable({ providedIn: 'root' })
export class ExecutableVersionPortAdapter implements ExecutableVersionPort {
    constructor(
        private executableVersionApi: OrgSdk.ExecutableVersionService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(ORG_ENV) private env: EnvMicroOrganization,
    ) {}

    getCurrentExecutableVersion(): Observable<ExecutableVersion> {
        this.appEvents.loaderVisiblePublish(true, 'executable-version');

        const source$: Observable<any> = this.shouldMock()
            ? of(executableVersionSdkMock.getCurrentExecutableVersion()).pipe(delay(360))
            : this.executableVersionApi.getCurrentExecutableVersion();

        return source$.pipe(
            map((resp) => mapSDKExecutableVersionToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'executable-version')),
        );
    }

    private shouldMock(): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.executableVersion?.getCurrent);
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapOrganizationErrorToMessage(err),
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
